import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric1,
  type DashboardMetric1Cycle,
} from '@/services/dashboardService';
import styled from 'styled-components';

const { Text } = Typography;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

// Good spread across the color wheel
function baseHueFromIndex(i: number) {
  return (i * 137.508) % 360; // golden angle
}

function shadeFromCycleHue(hue: number, t: number) {
  const saturation = 70;
  const lightness = clamp(78 - t * 40, 30, 85); // light -> dark
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

const MIN_CHART_H = 420;
const BAR_ROW_H = 36; // vertical space per cycle (bar row)
const CHART_PADDING_H = 120; // axes/labels/margins breathing room

const LEGEND_ITEM_W = 240; // average width per legend item
const LEGEND_ROW_H = 22; // per legend row height
const LEGEND_MIN_H = 24;
const LEGEND_MAX_ROWS = 30; // cap so chart doesn’t become massive

type ProcessKey = {
  key: string; // e.g. "cycle_<cycleId>_process_<processId>"
  cycleId: string;

  processId: string;
  processCode: string;
  processName: string;

  // used for shade generation inside the cycle
  shadeIndex: number;
  shadeCount: number;
};

export type EX1NumberOfCyclesAndProcessesHorizontalStackedBarChartProps = {
  /**
   * Option A (Ideal - Segmented) when true, Option B (Simple - Solid) when false
   */
  segmented?: boolean;

  /**
   * Constraint from requirement: best for <20 processes per cycle.
   * If exceeded, we auto-fall-back to solid mode.
   */
  maxSegmentsPerCycle?: number;

  /**
   * Chart container height for ResponsiveContainer
   */
  height?: number;

  /**
   * Chart title shown in the Card header
   */
  title?: string;
};

const SegmentedTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  // Only show segments that exist for this cycle (value > 0)
  const visible = payload.filter((p: any) => Number(p?.value) > 0);

  if (visible.length === 0) return null;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {label} (Count: {visible.length})
      </div>
      {visible.map((p: any) => (
        <div
          key={p.dataKey}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 4,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: p.color,
              display: 'inline-block',
            }}
          />
          <span>{p.name}</span>
        </div>
      ))}
    </div>
  );
};

const LICustom = styled.div`
  list-style-type: none;
    position: relative; 

  &:before {
    content: '■';
    position: absolute;
    left: -1.2em;         
    font-size: 1.1em;
  }
`;

export const EX1NumberOfCyclesAndProcessesHorizontalStackedBarChart: React.FC<
  EX1NumberOfCyclesAndProcessesHorizontalStackedBarChartProps
> = ({
  segmented = true,
  maxSegmentsPerCycle = 20,
  title = 'Number of Cycles and Processes',
}) => {
  const navigate = useNavigate();

  // Get annualReportId from Redux
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  // Local state (since you’re not using useQuery / react-query yet)
  const [cycles, setCycles] = useState<DashboardMetric1Cycle[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch Metric 1 data when annualReportId changes
  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setCycles([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric1(annualReportId);
        if (!isMounted) return;
        setCycles(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        const msg =
          err?.message ||
          err?.Message ||
          'Failed to load Metric 1 (Processes count breakdown by cycle).';
        setErrorMessage(msg);
        setCycles([]);
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  // Force solid mode if any cycle exceeds threshold
  const shouldForceSolid = useMemo(() => {
    return cycles.some(
      cycle => (cycle.processes?.length ?? 0) > maxSegmentsPerCycle,
    );
  }, [cycles, maxSegmentsPerCycle]);

  const effectiveSegmented = segmented && !shouldForceSolid;

  /**
   * Build stacked dataset for Option A:
   * Each cycle row becomes:
   * { cycleId, cycleName, totalCount, process_<id>: 1|0 ... }
   */
  const { segmentedData, processKeys, cycleHueMap } = useMemo(() => {
    const keys: ProcessKey[] = [];

    // stable hues per cycle based on displayed order
    const hueMap: Record<string, number> = {};
    cycles.forEach((c, idx) => {
      hueMap[c.cycleId] = baseHueFromIndex(idx);
    });

    const data = cycles.map(cycle => {
      const row: Record<string, any> = {
        cycleId: cycle.cycleId,
        cycleName: cycle.cycleCode + ' ' + cycle.cycleName,
        totalCount: cycle.processCount,
      };

      const procs = cycle.processes ?? [];

      // important: stable order inside a cycle (so shades don’t jump around)
      const sorted = [...procs].sort((a, b) =>
        (a.processCode ?? '').localeCompare(b.processCode ?? ''),
      );

      const n = sorted.length;

      sorted.forEach((p, i) => {
        const key = `cycle_${cycle.cycleId}_process_${p.processId}`;

        // this segment exists ONLY for this cycle row
        row[key] = 1;

        keys.push({
          key,
          cycleId: cycle.cycleId,
          processId: p.processId,
          processCode: p.processCode,
          processName: p.processName,
          shadeIndex: i,
          shadeCount: n,
        });
      });

      return row;
    });

    return { segmentedData: data, processKeys: keys, cycleHueMap: hueMap };
  }, [cycles]);

  /**
   * Build solid dataset for Option B:
   * { cycleId, cycleName, totalCount }
   */
  const solidData = useMemo(() => {
    return cycles.map(cycle => ({
      cycleId: cycle.cycleId,
      cycleName: cycle.cycleName,
      totalCount: cycle.processCount,
    }));
  }, [cycles]);

  const maxCount = useMemo(() => {
    return Math.max(0, ...cycles.map(c => c.processCount ?? 0));
  }, [cycles]);

  const { totalCycles, totalProcesses } = useMemo(() => {
    const totalCycles = cycles.length;

    // safest: sum processCount (already provided by API)
    const totalProcesses = cycles.reduce(
      (sum, c) => sum + (c.processCount ?? 0),
      0,
    );

    return { totalCycles, totalProcesses };
  }, [cycles]);

  const computedTitle =
    !loading && !errorMessage && cycles.length > 0
      ? `Number of Cycles (Total Count: ${totalCycles}) and Processes (Total Count: ${totalProcesses})`
      : title;

  /**
   * Routing helpers
   * Adjust these URLs to match your real Exhibit 3 route/params.
   */
  const goToCycleInExhibit3 = (cycleId: string) => {
    navigate('/process-hierarchy', { state: { selectedCycleId: cycleId } });
  };

  const goToProcessInExhibit3 = (_cycleId: string, processId: string) => {
    navigate('/process-hierarchy', { state: { selectedProcessId: processId } });
  };

  const isEmpty = !loading && !errorMessage && cycles.length === 0;

  // ----- Dynamic height (bars + legend) -----
  const computedHeight = useMemo(() => {
    const cycleCount = cycles.length;

    // 1) bars height based on number of cycles
    const barsHeight = cycleCount * BAR_ROW_H;

    

    // 2) legend height estimate (horizontal legend wraps into rows)
    //const highestLegendItemCount = effectiveSegmented ? Math.max(...procLengths) : 1;

    // Use your export stage width as a stable estimate. If your on-screen container
    // is usually narrower, change this to ~900.
    const containerWidthEstimate = 1100;

    const legendCols = Math.max(
      1,
      Math.floor(containerWidthEstimate / LEGEND_ITEM_W),
    );

    let extraRows = 0;

    cycles.map((cycle) => {
      extraRows += cycle.processCount / legendCols;
    });

    const legendRowsRaw = cycleCount + extraRows;

    //Math.ceil(legendItemCount / legendCols);
    const legendRows = Math.min(LEGEND_MAX_ROWS, legendRowsRaw);

    const legendHeight = Math.max(LEGEND_MIN_H, legendRows * LEGEND_ROW_H);

    // 3) final computed height
    return Math.max(MIN_CHART_H, barsHeight + legendHeight + CHART_PADDING_H);
  }, [cycles.length, effectiveSegmented, processKeys.length]);

  

  const renderLegend = () => {
    const keys: ProcessKey[] = [];

    cycles.map(cycle => {
      const row: Record<string, any> = {
        cycleId: cycle.cycleId,
        cycleName: cycle.cycleCode + ' ' + cycle.cycleName,
        totalCount: cycle.processCount,
      };

      const procs = cycle.processes ?? [];

      // important: stable order inside a cycle (so shades don’t jump around)
      const sorted = [...procs].sort((a, b) =>
        (a.processCode ?? '').localeCompare(b.processCode ?? ''),
      );

      const n = sorted.length;

      sorted.forEach((p, i) => {
        const key = `cycle_${cycle.cycleId}_process_${p.processId}`;

        // this segment exists ONLY for this cycle row
        row[key] = 1;

        keys.push({
          key,
          cycleId: cycle.cycleId,
          processId: p.processId,
          processCode: p.processCode,
          processName: p.processName,
          shadeIndex: i,
          shadeCount: n,
        });
      });
    })
  return (
    <ul>
      {
        cycles.map((entry, index) => (
          <div style={{display:"flex", paddingLeft:"6rem", flexWrap:"wrap"}}>
          
            {keys.filter(k => k.cycleId == entry.cycleId).map((key) => (
              <LICustom key={`item-${index}`} style={{marginLeft:"2rem", color:shadeFromCycleHue(cycleHueMap[key.cycleId], key.shadeCount <= 1 ? 0 : key.shadeIndex / (key.shadeCount - 1))}}>
              {key.processCode} {key.processName}
              </LICustom>
            ))}
            
            </div>
          
        ))
      }
    </ul>
  );
}

  return (
    <Card
      title={computedTitle}
      extra={
        shouldForceSolid ? (
          <Text type='secondary'>
            Segmented view disabled (one or more cycles exceed{' '}
            {maxSegmentsPerCycle} processes).
          </Text>
        ) : null
      }
    >
      <div style={{ height: computedHeight }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 1 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No cycles/processes were returned for this report.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={effectiveSegmented ? segmentedData : solidData}
              layout='vertical'
              margin={{ top: 8, right: 40, bottom: 24, left: 32 }}
            >
              <XAxis
                type='number'
                domain={[0, maxCount]}
                ticks={Array.from({ length: maxCount }, (_, i) => i + 1)}
                allowDecimals={false}
                label={{
                  value: 'Count of Processes per Cycle',
                  position: 'insideBottom',
                  offset: -10,
                }}
              />
              <YAxis
                type='category'
                dataKey='cycleName'
                width={150}
                label={{
                  value: 'Cycle Name',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -20,
                }}
              />

              <Tooltip content={<SegmentedTooltip />} />
              <Legend
                layout='horizontal'
                verticalAlign='bottom'
                align='center'
                wrapperStyle={{
                  paddingTop: 30,
                  overflow: 'visible',
                  maxHeight: computedHeight - 40,
                }}
                content={renderLegend}
              />

              {effectiveSegmented ? (
                // Option A: Segmented stacked bars (click segment -> process focus)
                <>
                  {processKeys.map(k => {
                    const hue = cycleHueMap[k.cycleId] ?? 210;
                    const t =
                      k.shadeCount <= 1 ? 0 : k.shadeIndex / (k.shadeCount - 1);
                    const fill = shadeFromCycleHue(hue, t);

                    return (
                      <Bar
                        key={k.key}
                        dataKey={k.key}
                        name={`${k.processCode} ${k.processName}`}
                        fill={fill}
                        stackId='cycleStack'
                        isAnimationActive={false}
                        onClick={(barData: any) => {
                          const cycleId = barData?.payload?.cycleId as
                            | string
                            | undefined;
                          if (!cycleId) return;

                          // in Option 2, if you clicked this Bar, it belongs to that cycle by construction
                          goToProcessInExhibit3(cycleId, k.processId);
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    );
                  })}
                </>
              ) : (
                // Option B: Solid bars (click bar -> cycle focus)
                <Bar
                  dataKey='totalCount'
                  name='Total Processes'
                  isAnimationActive={false}
                  style={{ cursor: 'pointer' }}
                  onClick={(barData: any) => {
                    const cycleId = barData?.payload?.cycleId as
                      | string
                      | undefined;
                    if (cycleId) goToCycleInExhibit3(cycleId);
                  }}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default EX1NumberOfCyclesAndProcessesHorizontalStackedBarChart;
